import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useActiveBrand } from "../hooks/useActiveBrand";
import { useOutputs } from "../hooks/useOutputs";
import { Badge } from "../components/ui/Badge";
import { Card } from "../components/ui/Card";
import { OperatorBrandGate } from "../components/layout/OperatorBrandGate";
import { NonWorkspacePage, PageEmptyState, PageErrorState, PageLoadingState } from "../components/layout/NonWorkspacePage";
import { buildBrandScopedPath } from "../lib/brand-navigation";
import { buildWorkspaceHandoffPath, workspaceReturnLocationState } from "../lib/workspace-return";
import { timeAgo, humanizeSlug } from "../lib/utils";
import { legacyRunJobLabel } from "../lib/task-run-presentation";
import { visibleWorkProductWarnings } from "../lib/output-warnings";
import { workProductWarningDefinition } from "../../../shared/types";
import type { WorkProductIndexOmissionSummary, WorkProductReadiness } from "../../../shared/types";

type OutputCategory = "drafts" | "brand_docs" | "research" | "diagnostics";

function readinessLabel(readiness: WorkProductReadiness): string {
  switch (readiness) {
    case "usable":
      return "Ready";
    case "usable_with_warnings":
      return "Warnings";
    case "unlinked":
      return "Unlinked";
    case "diagnostic_only":
      return "Diagnostic";
    case "failed":
      return "Failed";
    case "running":
      return "Running";
    default:
      return "Unknown";
  }
}

function readinessVariant(readiness: WorkProductReadiness): "success" | "warning" | "error" | "default" {
  switch (readiness) {
    case "usable":
      return "success";
    case "usable_with_warnings":
    case "unlinked":
    case "diagnostic_only":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

function warningText(code: string): string {
  const definition = workProductWarningDefinition(code);
  return `${definition.label}: ${definition.description}`;
}

function omissionText(omission: WorkProductIndexOmissionSummary): string {
  const artifactNoun = omission.count === 1 ? "artifact" : "artifacts";
  const outputNoun = omission.count === 1 ? "output" : "outputs";
  switch (omission.reason) {
    case "not_brand_scoped":
      return `${omission.count} agent ${artifactNoun} ${omission.count === 1 ? "is" : "are"} still user-scoped or missing a brand-linked session, so ${omission.count === 1 ? "it is" : "they are"} omitted from this brand index.`;
    case "missing_identity":
      return `${omission.count} agent ${artifactNoun} ${omission.count === 1 ? "is" : "are"} missing a usable key or id, so ${omission.count === 1 ? "it cannot" : "they cannot"} be indexed yet.`;
    case "failed_contract_check":
      return `${omission.count} ${outputNoun} ${omission.count === 1 ? "is" : "are"} missing required record fields, so ${omission.count === 1 ? "it is" : "they are"} omitted from this brand index until repaired.`;
    default:
      return `${omission.count} ${outputNoun} ${omission.count === 1 ? "is" : "are"} omitted from this brand index.`;
  }
}

export function sourceLabel(output: {
  family: string;
  source_run_id?: string | null;
  task_slug: string;
  source_job_label?: string | null;
}): string {
  if (output.source_run_id) return output.source_job_label ?? legacyRunJobLabel(output.task_slug);
  switch (output.family) {
    case "brand_document":
      return "Brand document";
    case "research_output":
      return "Research output";
    case "market_research_artifact":
      return "Market research";
    case "current_events_research_artifact":
      return "Current-events research";
    case "agent_artifact":
      return "Agent artifact";
    default:
      return "Inferred from storage";
  }
}

function outputCategory(output: { family: string }): OutputCategory {
  switch (output.family) {
    case "campaign_document":
      return "drafts";
    case "brand_document":
      return "brand_docs";
    case "research_output":
    case "market_research_artifact":
    case "current_events_research_artifact":
      return "research";
    default:
      return "diagnostics";
  }
}

function outputCategoryLabel(category: OutputCategory): string {
  switch (category) {
    case "drafts":
      return "Drafts";
    case "brand_docs":
      return "Brand docs";
    case "research":
      return "Research";
    default:
      return "Diagnostics";
  }
}

export function Outputs() {
  const { activeBrandId: brandId, isLoading: brandLoading } = useActiveBrand();
  const { data, isLoading, error } = useOutputs(brandId);
  const outputs = data?.data.outputs ?? [];
  const warnings = data?.data.warnings ?? [];
  const omissions = data?.data.omissions ?? [];
  const visibleWarnings = visibleWorkProductWarnings(warnings, omissions);
  const supportedFamilies = data?.data.families.filter((family) => family.supported) ?? [];
  const latestOutput = outputs[0] ?? null;
  const groupedOutputs = outputs.reduce<Record<OutputCategory, typeof outputs>>(
    (acc, output) => {
      acc[outputCategory(output)].push(output);
      return acc;
    },
    { drafts: [], brand_docs: [], research: [], diagnostics: [] },
  );

  useEffect(() => {
    document.title = "Outputs — HelloConvo";
  }, []);

  if (!brandLoading && !brandId) {
    return (
      <OperatorBrandGate
        title="Choose a brand before opening outputs"
        description="Outputs are indexed per brand. Select the brand you intend to inspect so document access and follow-on actions stay scoped correctly."
      />
    );
  }

  const header = {
    eyebrow: "Outputs",
    title: "Outputs",
    description: "A reusable library of the work your agents have produced for this brand.",
    breadcrumbs: [
      { label: "Workspace", href: "/home" },
      { label: "Outputs" },
    ],
    // The count is a claim; render it only once the list actually loaded (P3).
    badges: brandLoading || isLoading || error ? undefined : (
      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
        {outputs.length} output{outputs.length === 1 ? "" : "s"}
      </span>
    ),
  };

  if (brandLoading || isLoading) {
    return (
      <NonWorkspacePage width="wide" header={header}>
        <PageLoadingState>Loading outputs…</PageLoadingState>
      </NonWorkspacePage>
    );
  }

  if (error) {
    return (
      <NonWorkspacePage width="wide" header={header}>
        <PageErrorState description={error instanceof Error ? error.message : "Failed to load outputs"} />
      </NonWorkspacePage>
    );
  }

  return (
    <NonWorkspacePage width="wide" header={header}>

      {latestOutput ? (
        <Card className="rounded-[24px] border border-brand-200 bg-brand-50/60 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">Latest output</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge variant={readinessVariant(latestOutput.readiness)}>
                  {readinessLabel(latestOutput.readiness)}
                </Badge>
                <Badge>{outputCategoryLabel(outputCategory(latestOutput))}</Badge>
                <span className="text-xs text-slate-500">{timeAgo(latestOutput.created_at)}</span>
              </div>
              <h2 className="mt-4 text-2xl font-semibold text-slate-900">{latestOutput.title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {sourceLabel(latestOutput)}{latestOutput.source_run_id ? " generated this artifact." : " is the current artifact source."}
              </p>
              {(latestOutput.warnings?.length ?? 0) > 0 ? (
                <p className="mt-2 text-sm text-amber-800">
                  {(latestOutput.warnings ?? []).map(warningText).join("; ")}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={buildWorkspaceHandoffPath(latestOutput.route_path ?? `/outputs/${encodeURIComponent(latestOutput.family)}/${encodeURIComponent(latestOutput.key)}`, brandId, "outputs")}
                state={workspaceReturnLocationState("outputs")}
                className="inline-flex items-center rounded-xl bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
              >
                Open in workspace
              </Link>
              {latestOutput.source_run_id ? (
                <Link
                  to={buildWorkspaceHandoffPath(`/runs/${encodeURIComponent(latestOutput.source_run_id)}`, brandId, "outputs")}
                  state={workspaceReturnLocationState("outputs")}
                  className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Open run in workspace
                </Link>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}

      {visibleWarnings.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-medium">Some outputs need traceability review.</p>
          <p className="mt-1">
            {visibleWarnings.map(warningText).join("; ")}
          </p>
        </div>
      )}

      {omissions.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <p className="font-medium">Some agent artifacts are not shown in this brand index yet.</p>
          <p className="mt-1">
            {omissions.map(omissionText).join(" ")}
          </p>
        </div>
      )}

      {outputs.length === 0 ? (
        <PageEmptyState
          title="No outputs for this brand yet"
          description="Published drafts, research, and saved brand documents will appear here as agents create them."
        />
      ) : (
        <div className="space-y-4">
          {(Object.entries(groupedOutputs) as Array<[OutputCategory, typeof outputs]>)
            .filter(([, items]) => items.length > 0)
            .map(([category, items]) => (
              <Card key={category} className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{outputCategoryLabel(category)}</p>
                    <h2 className="mt-1 text-xl font-semibold text-slate-900">{items.length} artifact{items.length === 1 ? "" : "s"}</h2>
                  </div>
                </div>
                <div className="grid gap-3">
                  {items.map((output) => (
                    <div key={`${output.family}:${output.key}`} className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <Badge variant={readinessVariant(output.readiness)}>
                              {readinessLabel(output.readiness)}
                            </Badge>
                            <span className="text-xs text-slate-500">{humanizeSlug(output.family)}</span>
                            <span className="text-xs text-slate-500">{timeAgo(output.created_at)}</span>
                          </div>
                          <Link
                            to={buildWorkspaceHandoffPath(output.route_path ?? `/outputs/${encodeURIComponent(output.family)}/${encodeURIComponent(output.key)}`, brandId, "outputs")}
                            state={workspaceReturnLocationState("outputs")}
                            className="font-medium text-brand-700 hover:text-brand-800 hover:underline"
                          >
                            {output.title}
                          </Link>
                          <p className="mt-1 text-sm text-slate-600">{sourceLabel(output)}</p>
                          <p className="mt-1 break-all text-xs text-slate-500">{output.key}</p>
                          {(output.warnings?.length ?? 0) > 0 ? (
                            <p className="mt-2 text-xs text-amber-700">
                              {(output.warnings ?? []).map(warningText).join("; ")}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Link
                            to={buildWorkspaceHandoffPath(output.route_path ?? `/outputs/${encodeURIComponent(output.family)}/${encodeURIComponent(output.key)}`, brandId, "outputs")}
                            state={workspaceReturnLocationState("outputs")}
                            className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                          >
                            Open in workspace
                          </Link>
                          {output.source_run_id ? (
                            <Link
                              to={buildWorkspaceHandoffPath(`/runs/${encodeURIComponent(output.source_run_id)}`, brandId, "outputs")}
                              state={workspaceReturnLocationState("outputs")}
                              className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                              Open run in workspace
                            </Link>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
        </div>
      )}

      <details className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
        <summary className="cursor-pointer text-sm font-medium text-slate-900">Supporting diagnostics</summary>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <Card className="rounded-[20px] border border-slate-200 bg-white shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Indexed outputs</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{outputs.length}</p>
            {omissions.length > 0 && (
              <p className="mt-1 text-xs text-amber-700">
                {omissions.reduce((sum, omission) => sum + omission.count, 0)} omitted
              </p>
            )}
          </Card>
          <Card className="rounded-[20px] border border-slate-200 bg-white shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Integrated families</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{supportedFamilies.length}</p>
            <p className="mt-1 text-xs text-slate-500">
              {supportedFamilies.map((family) => humanizeSlug(family.family)).join(", ") || "None"}
            </p>
          </Card>
          <Card className="rounded-[20px] border border-slate-200 bg-white shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Index strategy</p>
            <p className="mt-2 text-lg font-semibold text-slate-900">Computed on read</p>
            <p className="mt-1 text-xs text-slate-500">No separate registry table yet.</p>
          </Card>
        </div>
      </details>
    </NonWorkspacePage>
  );
}
